import { Trash2 } from 'lucide-react';
import type {
  UseImageEditorResult,
  QRCodeBlock,
} from '../../hooks/useImageEditor';
import { ICON_QR_SIZE_MIN } from '../../lib/image-editor/overlayLimits';

interface QRCodePanelProps {
  editor: UseImageEditorResult;
  /** 尺寸上限（依輸出畫布像素）；未傳時用預設 400 */
  sizeMax?: number;
}

/**
 * QR Code 設定：多個 QR 項目，每項可設內容、尺寸；在畫布上可拖拉定位，雙擊預覽可開啟本面板。
 */
export function QRCodePanel({ editor, sizeMax = 400 }: QRCodePanelProps) {
  const {
    qrCodeItems,
    addQrCodeItem,
    removeQrCodeItem,
    updateQrCodeItem,
    handleResetQrCode,
  } = editor;
  const maxSize = Math.max(ICON_QR_SIZE_MIN, sizeMax);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
          QR Code 項目
        </span>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={addQrCodeItem}
            className="rounded border border-primary bg-white px-3 py-1.5 text-sm text-primary hover:bg-primary/5 dark:border-primary dark:bg-gray-800 dark:hover:bg-primary/10"
          >
            新增 QR Code
          </button>
          {qrCodeItems.length > 0 && (
            <button
              type="button"
              onClick={handleResetQrCode}
              className="rounded px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-200 dark:text-gray-400 dark:hover:bg-gray-700"
            >
              全部清除
            </button>
          )}
        </div>
      </div>

      {qrCodeItems.length === 0 ? (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          點「新增 QR
          Code」加入項目，輸入內容後可於預覽中拖拉定位；雙擊預覽可開啟本設定。
        </p>
      ) : (
        <ul className="space-y-3">
          {qrCodeItems.map((block: QRCodeBlock) => {
            const value = Math.max(
              ICON_QR_SIZE_MIN,
              Math.min(maxSize, block.size)
            );
            return (
              <li
                key={block.id}
                className="rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-600 dark:bg-gray-800/50"
              >
                <div className="mb-2 flex items-center justify-between gap-2">
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    QR #{qrCodeItems.findIndex(b => b.id === block.id) + 1}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeQrCodeItem(block.id)}
                    className="rounded p-1 text-gray-500 hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-900/30 dark:hover:text-red-400"
                    aria-label="刪除此 QR Code"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                <div className="space-y-2">
                  <div>
                    <label className="mb-0.5 block text-xs text-gray-600 dark:text-gray-400">
                      內容（URL 或文字）
                    </label>
                    <input
                      type="text"
                      value={block.content}
                      onChange={e =>
                        updateQrCodeItem(block.id, {
                          content: e.target.value,
                        })
                      }
                      placeholder="https://example.com 或任意文字"
                      className="w-full rounded border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-900 placeholder-gray-400 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 dark:placeholder-gray-500"
                      maxLength={500}
                      aria-label="QR Code 內容"
                    />
                  </div>
                  <div>
                    <div className="mb-0.5 flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                      <span>大小 (px)</span>
                      <span>{block.size}</span>
                    </div>
                    <input
                      type="range"
                      min={ICON_QR_SIZE_MIN}
                      max={maxSize}
                      step={4}
                      value={value}
                      onChange={e =>
                        updateQrCodeItem(block.id, {
                          size: Number(e.target.value),
                        })
                      }
                      className="w-full accent-primary"
                      aria-label="QR Code 大小"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="mb-0.5 block text-xs text-gray-600 dark:text-gray-400">
                        碼色（前景）
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={(block.darkColor ?? '#000000').replace(
                            /^#?([0-9a-fA-F]{6})$/,
                            '#$1'
                          )}
                          onChange={e =>
                            updateQrCodeItem(block.id, {
                              darkColor: e.target.value,
                            })
                          }
                          className="h-8 w-10 cursor-pointer rounded border border-gray-300 bg-white p-0 dark:border-gray-600 dark:bg-gray-700"
                          aria-label="QR 碼色"
                        />
                        <input
                          type="text"
                          value={block.darkColor ?? '#000000'}
                          onChange={e =>
                            updateQrCodeItem(block.id, {
                              darkColor: e.target.value || '#000000',
                            })
                          }
                          placeholder="#000000"
                          className="min-w-0 flex-1 rounded border border-gray-300 bg-white px-2 py-1 text-xs text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                          maxLength={20}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="mb-0.5 block text-xs text-gray-600 dark:text-gray-400">
                        背景色
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={(block.lightColor ?? '#ffffff').replace(
                            /^#?([0-9a-fA-F]{6})$/,
                            '#$1'
                          )}
                          onChange={e =>
                            updateQrCodeItem(block.id, {
                              lightColor: e.target.value,
                            })
                          }
                          className="h-8 w-10 cursor-pointer rounded border border-gray-300 bg-white p-0 dark:border-gray-600 dark:bg-gray-700"
                          aria-label="QR 背景色"
                        />
                        <input
                          type="text"
                          value={block.lightColor ?? '#ffffff'}
                          onChange={e =>
                            updateQrCodeItem(block.id, {
                              lightColor: e.target.value || '#ffffff',
                            })
                          }
                          placeholder="#ffffff"
                          className="min-w-0 flex-1 rounded border border-gray-300 bg-white px-2 py-1 text-xs text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                          maxLength={20}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
